import { Inbox, Loader2, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { errorMessage } from "@/i18n";

/** Loading, error and empty states, designed rather than left as defaults. */
export function Loading({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex items-center gap-3 py-12 text-muted-foreground">
      <Loader2 aria-hidden="true" className="size-5 animate-spin" />
      <span>{label ?? t("states.loading")}</span>
    </div>
  );
}

/** A table's shape while its rows load; announced as loading. */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex flex-col gap-3">
      <span className="sr-only">{t("states.loading")}</span>
      <Skeleton className="h-9 w-64" />
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="border-b border-border bg-muted/60 px-3 py-3">
          <Skeleton className="h-3 w-1/3" />
        </div>
        {Array.from({ length: rows }, (_, index) => (
          <div
            key={index}
            className="flex items-center gap-4 border-b border-border px-3 py-3 last:border-0"
          >
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-4 w-16 rounded-full" />
            <Skeleton className="h-4 w-1/5" />
            <Skeleton className="ml-auto h-4 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Dashboard figures while they load. */
export function CardsSkeleton({ count = 4 }: { count?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
      <span className="sr-only">{t("states.loading")}</span>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5"
        >
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
      ))}
    </div>
  );
}

export function LoadFailed({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-4 py-8">
      <Alert tone="error" title={t("states.errorTitle")}>
        {errorMessage(error)}
      </Alert>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          {t("states.retry")}
        </Button>
      ) : null}
    </div>
  );
}

export function Empty({
  title,
  children,
  icon: Icon = Inbox,
}: {
  title: string;
  children?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
      <span className="mb-3 flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon aria-hidden="true" className="size-5" />
      </span>
      <p className="font-semibold">{title}</p>
      {children ? (
        <div className="mt-1.5 max-w-md text-sm text-muted-foreground">{children}</div>
      ) : null}
    </div>
  );
}
